import { ReactNode } from 'react';
import { AppShell } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import demoClasses from './DemoLayout.module.css';
import MainHeader from '../components/MainHeader';
import MainSidebar from '../components/MainSidebar';
import MainFooter from '../components/MainFooter';
import MainBackground from '../components/MainBackground';
import ScreenLockModal from '../components/ScreenLockModal';
import { usePreferencesStore } from '../../stores';
import { usePresenceManager } from '../../hooks/usePresenceManager';
import { PresenceStatus } from '@actschurch/ccms-sdk';

interface DemoLayoutProps {
  children: ReactNode;
  showSidebar?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
}

/**
 * DemoLayout Component
 * Layout orchestrator specifically for Demo pages
 * Provides header, comprehensive demo sidebar with all pages, footer, and content area
 */
export default function DemoLayout({
  children,
  showSidebar = true,
  showHeader = true,
  showFooter = true,
}: DemoLayoutProps) {
  const sidebarCollapsed = usePreferencesStore((state) => state.sidebarCollapsed);
  const toggleSidebar = usePreferencesStore((state) => state.toggleSidebar);
  const setSidebarCollapsed = usePreferencesStore((state) => state.setSidebarCollapsed);
  const menuIconOnly = usePreferencesStore((state) => state.menuIconOnly);
  const menuAutoHide = usePreferencesStore((state) => state.menuAutoHide);

  // Screen lock state - persisted in preferences
  const isLocked = usePreferencesStore((state) => state.screenLocked);
  const setScreenLocked = usePreferencesStore((state) => state.setScreenLocked);
  const setPresenceStatus = usePreferencesStore((state) => state.setPresenceStatus);

  // Presence integration
  const { isConnected, currentUserPresence, updateStatus } = usePresenceManager();

  // Auto-close sidebar when clicking outside (only when auto-hide is enabled and sidebar is open)
  const sidebarRef = useClickOutside(() => {
    if (menuAutoHide && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  });

  const handleLockScreen = async () => {
    // Lock the screen
    setScreenLocked(true);

    // Set presence to Away when locking
    try {
      setPresenceStatus('Away' as unknown as PresenceStatus);

      // Check connection state before attempting to update
      console.log('[DemoLayout] Presence state:', {
        isConnected: isConnected,
        currentUserPresence: currentUserPresence,
      });

      if (isConnected) {
        console.log('[DemoLayout] Attempting to update presence to Away...');
        await updateStatus('Away' as unknown as PresenceStatus);
        console.log('[DemoLayout] Presence successfully set to Away (screen locked)');
      } else {
        console.warn('[DemoLayout] Presence hub not connected, status saved locally only');
      }
    } catch (error: any) {
      console.error('[DemoLayout] Failed to update presence on lock:', {
        message: error?.message,
        name: error?.name,
        cause: error?.cause,
        fullError: error,
      });
      // Continue anyway - the local preference is saved
    }
  };

  const handleUnlockScreen = async () => {
    // Unlock the screen
    setScreenLocked(false);

    // Set presence back to Online when unlocking
    try {
      setPresenceStatus('Online' as unknown as PresenceStatus);

      // Check connection state before attempting to update
      console.log('[DemoLayout] Presence state:', {
        isConnected: isConnected,
        currentUserPresence: currentUserPresence,
      });

      if (isConnected) {
        console.log('[DemoLayout] Attempting to update presence to Online...');
        await updateStatus('Online' as unknown as PresenceStatus);
        console.log('[DemoLayout] Presence successfully set to Online (screen unlocked)');
      } else {
        console.warn('[DemoLayout] Presence hub not connected, status saved locally only');
      }
    } catch (error: any) {
      console.error('[DemoLayout] Failed to update presence on unlock:', {
        message: error?.message,
        name: error?.name,
        cause: error?.cause,
        fullError: error,
      });
      // Continue anyway - the local preference is saved
    }
  };

  return (
    <>
      {/* Screen Lock Modal - Render first for proper z-index */}
      <ScreenLockModal opened={isLocked} onUnlock={handleUnlockScreen} />

      <MainBackground>
        <AppShell
          header={showHeader ? { height: 60 } : undefined}
          navbar={
            showSidebar
              ? {
                  width: menuIconOnly ? { sm: 100, lg: 110 } : { sm: 200, lg: 250 },
                  breakpoint: 'sm',
                  collapsed: { mobile: true, desktop: sidebarCollapsed },
                }
              : undefined
          }
          footer={showFooter ? { height: 60 } : undefined}
          padding="md"
        >
          {/* Header */}
          {showHeader && (
            <AppShell.Header>
              <MainHeader
                onSidebarToggle={toggleSidebar}
                sidebarCollapsed={sidebarCollapsed}
                onLockScreen={handleLockScreen}
                showBurger={showSidebar}
              />
            </AppShell.Header>
          )}

          {/* Sidebar */}
          {showSidebar && (
            <AppShell.Navbar
              ref={sidebarRef}
              className={demoClasses.navbar}
            >
              <MainSidebar domainKey="demo" />
            </AppShell.Navbar>
          )}

          {/* Main Content */}
          <AppShell.Main>{children}</AppShell.Main>

          {/* Footer */}
          {showFooter && (
            <AppShell.Footer>
              <MainFooter />
            </AppShell.Footer>
          )}
        </AppShell>
      </MainBackground>
    </>
  );
}
